// Modified DynamicReport.js - Main parent component with status logic
import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  CardBody,
  Col,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import FalconCardHeader from 'components/common/FalconCardHeader';
import useApi from 'hooks/useApi';
import {
  getExportAllData,
  getFarmTableData
} from 'api/endpoints/TerrestrialServices';
import { get, isEmpty } from 'lodash';
import * as XLSX from 'xlsx';
import DynamicReportTable from './DynamicReportTable';
import { useNavigate } from 'react-router-dom';
import { getTenantFromToken } from 'utils/routeUtils';
import { SERVICES } from 'api/common/constants';
import { toast } from 'react-toastify';
import { tableEvents, TABLE_EVENTS } from './TableEventBus';

function DynamicReport({
  apiPath,
  title,
  fileName,
  editPath,
  editKey,
  addPath,
  createPath,
  viewDetailsPath,
  buttons = [],
  forWeather = false,
  dropdownFilter = false,
  dropdownData = [],
  payloadmode = 'nested',
  dateFilter,
  dateRangePicker,
  globalSearch = true,
  district_ids = [],
  errorMsg,
  responsive = false,
  cardTittles = [],
  showHeader = true,
  showFooter = true,
  service = SERVICES.DIGITIZATION,
  limit,
  statusUpdateFn,
  statusField = 'is_active',
  idField = 'id',
  exportKey = ''
}) {
  const tenant = getTenantFromToken();
  const getExportAllApi = useApi(getExportAllData);
  const getFarmTableDataAPI = useApi(getFarmTableData);

  const [showFilter, setShowFilter] = useState(false);
  const [exportdata, setExportData] = useState(null);
  const [show, setShow] = useState(false);
  const [target, setTarget] = useState(null);
  const navigate = useNavigate();

  // Status update handler that emits events to child table components
  const handleStatus = useCallback(
    async (status, rowData) => {
      try {
        if (!statusUpdateFn) {
          console.warn('No status update function provided for this report');
          return;
        }

        // Get the ID from the specified field
        const recordId = Array.isArray(idField)
          ? idField.map(key => rowData[key]).join('/')
          : rowData[idField];

        if (!recordId) {
          console.error(`ID field '${idField}' not found in row data`);
          return;
        }

        // Convert status to boolean for the API
        const isActive = status === 'Activate' ? true : false;
        console.log('DynamicReport: Updating status:', {
          recordId,
          isActive,
          currentStatus: rowData[statusField]
        });

        // 1. Emit optimistic update for instant UI feedback to both table components
        tableEvents.emit(TABLE_EVENTS.STATUS_UPDATE_OPTIMISTIC, {
          recordId,
          rowData,
          newStatus: isActive,
          statusField,
          idField,
          action: status
        });

        // 2. Make API call
        const response = await statusUpdateFn(recordId, isActive);

        if (
          response &&
          (response.success || response.status === 200 || response.data)
        ) {
          toast.success(
            response.data?.results || `Status updated successfully`
          );
          console.log(
            'DynamicReport: Status updated successfully',
            response.data
          );

          // 3. Emit success event - causes both table components to refresh from server
          tableEvents.emit(TABLE_EVENTS.STATUS_UPDATE_SUCCESS, {
            recordId,
            newStatus: isActive,
            statusField,
            idField,
            action: status,
            response
          });

          console.log(
            `DynamicReport: ${status} operation completed successfully`
          );
        } else {
          console.error('DynamicReport: Failed to update status:', response);
          toast.error('Failed to update status');

          // 4. Emit failure event - causes table components to revert optimistic update
          tableEvents.emit(TABLE_EVENTS.STATUS_UPDATE_FAILED, {
            recordId,
            originalData: rowData,
            statusField,
            idField
          });
        }
      } catch (error) {
        console.error('DynamicReport: Error updating status:', error);
        toast.error('Error updating status');

        // Emit failure event
        tableEvents.emit(TABLE_EVENTS.STATUS_UPDATE_FAILED, {
          recordId: Array.isArray(idField)
            ? idField.map(key => rowData[key]).join('/')
            : rowData[idField],
          originalData: rowData,
          statusField,
          idField
        });
      }
    },
    [statusUpdateFn, statusField, idField]
  );

  // Enhanced action handler that includes status updates
  const handleAction = useCallback(
    (action, rowData) => {
      if (action === 'Activate' || action === 'Deactivate') {
        handleStatus(action, rowData);
      }
      // Let other actions flow through to original handlers
      return {
        action,
        rowData,
        handled: action === 'Activate' || action === 'Deactivate'
      };
    },
    [handleStatus]
  );

  const handleFilterToggle = () => {
    setShowFilter(!showFilter);
  };

  // Update block list when blocks API data changes
  useEffect(() => {
    const data = get(getExportAllApi, 'data.results', []);
    if (!isEmpty(data)) {
      const s3Link = data;
      downloadFile(s3Link);
    }
  }, [getExportAllApi.data]);

  // Function to download the file from S3 link
  const downloadFile = url => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', '');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const handleDownload = () => {
    const excelData = convertToExcelData(exportdata, fileName);
    const filename = `${fileName}`;
    downloadExcel(excelData, filename);
  };

  const downloadExcel = (excelData, filename) => {
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    const totalColumns = excelData[2]?.length || 1;
    const lastCol = totalColumns + 2;
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } }];
    const wb = XLSX.utils.book_new();
    const sheetName = filename.replace('.xlsx', '');
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, filename);
  };

  const convertToExcelData = (data, fileName) => {
    if (!data || data.length === 0) return [];
    const now = new Date();
    const timestamp = now
      .toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
      .replace(',', ' at');
    const readableName = fileName
      .replace('.xlsx', '')
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
    const reportTitle = `${readableName} - Generated on ${timestamp} from the FARMSIO Application`;

    const isSMSLogLike = data.some(
      row =>
        row.extra &&
        typeof row.extra === 'object' &&
        Array.isArray(row.extra.data)
    );

    let headers = [];
    let dataRows = [];

    if (isSMSLogLike) {
      const excludeKeys = ['id', 'action', 'modalButtons'];
      const extraColumns = new Set();

      // Collect all unique keys from extra.data
      data.forEach(row => {
        const extraData = row.extra?.data;
        if (Array.isArray(extraData)) {
          extraData.forEach(entry => {
            Object.keys(entry || {}).forEach(key => {
              extraColumns.add(key); // Store the original key as-is
            });
          });
        }
      });

      const processedRows = [];

      data.forEach(row => {
        const baseRow = {};
        for (const key in row) {
          if (!excludeKeys.includes(key) && key !== 'extra') {
            baseRow[key] = row[key];
          }
        }

        const extraData = row.extra?.data;

        if (Array.isArray(extraData) && extraData.length > 0) {
          extraData.forEach(extraEntry => {
            const fullRow = { ...baseRow };
            [...extraColumns].forEach(col => {
              fullRow[col] = extraEntry[col] ?? ''; // Use the key directly
            });
            processedRows.push(fullRow);
          });
        } else {
          const fullRow = { ...baseRow };
          [...extraColumns].forEach(col => {
            fullRow[col] = '';
          });
          processedRows.push(fullRow);
        }
      });

      headers = Object.keys(processedRows[0] || {});
      dataRows = processedRows.map(row => headers.map(key => row[key] ?? ''));
    } else {
      headers = Object.keys(data[0] || {});
      dataRows = data.map(item => headers.map(header => item[header] ?? ''));
    }

    return [[reportTitle], [], headers, ...dataRows];
  };

  const handleOpenModal = async (mode, rowData) => {
    let params = '';
    if (Array.isArray(editKey)) {
      params = editKey.map(key => rowData[key]).join('/');
    } else {
      params = rowData[editKey];
    }
    navigate(`/${tenant}${editPath}${params}`);
  };

  const handleAddOpenModal = async (mode, rowData) => {
    let params = '';
    if (Array.isArray(editKey)) {
      params = editKey.map(key => rowData[key]).join('/');
    } else {
      params = rowData[editKey];
    }
    navigate(`/${tenant}${addPath}${params}`);
  };

  const handleCreateOpenModal = async (mode, rowData) => {
    let params = '';
    if (Array.isArray(editKey)) {
      params = editKey.map(key => rowData[key]).join('/');
    }
    navigate(`/${tenant}${createPath}`);
  };

  const handleViewDetails = async (mode, rowData) => {
    let params = '';
    if (Array.isArray(editKey)) {
      params = editKey.map(key => rowData[key]).join('/');
    } else {
      params = rowData[editKey];
    }
    navigate(`/${tenant}${viewDetailsPath}${params}`);
  };

  // Enhanced action handlers that work with the new event system
  const enhancedOnEdit = (action, rowData) => {
    const result = handleAction(action, rowData);
    if (!result.handled) {
      handleOpenModal('edit', rowData);
    }
  };

  const enhancedOnAdd = (action, rowData) => {
    const result = handleAction(action, rowData);
    if (!result.handled) {
      handleAddOpenModal('add', rowData);
    }
  };

  const enhancedOnViewDetails = (action, rowData) => {
    const result = handleAction(action, rowData);
    if (!result.handled) {
      handleViewDetails('viewDetails', rowData);
    }
  };

  const enhanceOnCreate = (action, rowData) => {
    const result = handleAction(action, rowData);
    if (!result.handled) {
      handleCreateOpenModal('create', rowData);
    }
  };

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const divStyle =
    windowWidth >= 768 ? { height: '85dvh', overflow: 'hidden' } : {};

  return (
    <div style={divStyle}>
      <DynamicReportTable
        key={apiPath}
        hyperlink={false}
        showFilter={showFilter}
        responsive={responsive}
        cardTittles={cardTittles}
        setExportData={setExportData}
        onEdit={enhancedOnEdit}
        onAdd={enhancedOnAdd}
        onCreate={enhanceOnCreate}
        onViewDetails={enhancedOnViewDetails}
        apiPath={apiPath}
        forWeather={forWeather}
        dateFilter={dateFilter}
        dropdownFilter={dropdownFilter}
        dropdownData={dropdownData}
        payloadmode={payloadmode}
        district_ids={district_ids}
        errorMsg={errorMsg}
        globalSearch={globalSearch}
        dateRangePicker={dateRangePicker}
        showHeader={showHeader}
        showFooter={showFooter}
        limit={limit}
        buttons={buttons}
        title={title}
        handleDownload={handleDownload}
        service={service}
        statusUpdateFn={statusUpdateFn}
        statusField={statusField}
        idField={idField}
        exportKey={exportKey}
        // Pass the enhanced action handler
        parentActionHandler={handleAction}
      />
    </div>
  );
}

DynamicReport.propTypes = {
  apiPath: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  fileName: PropTypes.string.isRequired,
  errorMsg: PropTypes.string,
  editPath: PropTypes.string,
  addPath: PropTypes.string,
  createPath: PropTypes.string,
  viewDetailsPath: PropTypes.string,
  editKey: PropTypes.string,
  forWeather: PropTypes.bool,
  service: PropTypes.string,
  dateFilter: PropTypes.bool,
  dropdownFilter: PropTypes.bool,
  payloadmode: PropTypes.string,
  dropdownData: PropTypes.arrayOf(PropTypes.object),
  dateRangePicker: PropTypes.bool,
  globalSearch: PropTypes.bool,
  showHeader: PropTypes.bool,
  showFooter: PropTypes.bool,
  limit: PropTypes.number,
  district_ids: PropTypes.arrayOf(PropTypes.number),
  responsive: PropTypes.bool,
  cardTittles: PropTypes.arrayOf(PropTypes.string),
  exportKey: PropTypes.string,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      enabled: PropTypes.bool,
      buttonName: PropTypes.string,
      redirectPath: PropTypes.string
    })
  ),
  statusUpdateFn: PropTypes.func,
  statusField: PropTypes.string,
  idField: PropTypes.oneOfType([PropTypes.string, PropTypes.array])
};

export default DynamicReport;
